// Path gates: where scrubbing forward REQUIRES a decision. An exclusive frame
// (if / try) with no explicit branch pick silently defaults to its first arm —
// stepping into it should instead pause and ask, so the user consciously
// chooses the path being walked. Inclusive frames (select, parallel) run all
// branches and never gate.
//
// Pure projections over the WalkResult tree; no React.

import type { NodeId, WalkNode, WalkResult } from "@archlang/engine";

export interface PathGate {
  readonly frameId: NodeId;
  readonly kind: "if" | "try";
  /** Arm labels in declaration order — the choices offered. */
  readonly labels: readonly string[];
  /** The arm the walker defaults to (first declared). */
  readonly defaultLabel: string;
}

/**
 * canonical stepIndex → the exclusive frames enclosing that step, outermost
 * first. Only steps of the SELECTED walk appear (the tree carries children
 * for selected arms only), which is exactly what advance-gating needs.
 */
export function buildPathGates(walk: WalkResult): ReadonlyMap<number, readonly PathGate[]> {
  const out = new Map<number, PathGate[]>();
  const visit = (nodes: readonly WalkNode[], chain: readonly PathGate[]): void => {
    for (const n of nodes) {
      switch (n.kind) {
        case "step":
          if (chain.length > 0) out.set(n.stepIndex, [...chain]);
          break;
        case "if": {
          const labels = [
            ...n.branches.map((b) => b.label),
            ...(n.elseBranch ? [n.elseBranch.label] : []),
          ];
          const gate: PathGate = { frameId: n.nodeId, kind: "if", labels, defaultLabel: labels[0] ?? "" };
          for (const b of n.branches) visit(b.children, [...chain, gate]);
          if (n.elseBranch) visit(n.elseBranch.children, [...chain, gate]);
          break;
        }
        case "try": {
          const labels = [n.tryBranch.label, ...n.catches.map((c) => c.label)];
          const gate: PathGate = { frameId: n.nodeId, kind: "try", labels, defaultLabel: labels[0] ?? "try" };
          visit(n.tryBranch.children, [...chain, gate]);
          for (const c of n.catches) visit(c.children, [...chain, gate]);
          break;
        }
        case "switch":
          for (const c of n.cases) visit(c.children, chain); // inclusive — no gate
          break;
        case "parallel":
          for (const b of n.branches) visit(b.children, chain); // all run — no gate
          break;
        case "each":
          visit(n.children, chain);
          break;
        case "do":
          if (n.body) visit(n.body, chain);
          break;
        case "terminator":
          break;
      }
    }
  };
  visit(walk.tree, []);
  return out;
}

/**
 * The gate an advance from `fromIndex` to `toIndex` must stop at: the
 * outermost exclusive frame that (a) encloses the destination, (b) did NOT
 * enclose the origin (we are ENTERING it — moving within a frame never
 * re-asks), and (c) has no explicit pick recorded. Null → advance freely.
 */
export function gateOnAdvance(
  gates: ReadonlyMap<number, readonly PathGate[]>,
  explicit: ReadonlyMap<NodeId, string>,
  fromIndex: number | null,
  toIndex: number,
): PathGate | null {
  const toGates = gates.get(toIndex);
  if (!toGates || toGates.length === 0) return null;
  const fromIds = new Set((fromIndex !== null ? gates.get(fromIndex) ?? [] : []).map((g) => g.frameId));
  for (const g of toGates) {
    if (fromIds.has(g.frameId)) continue;
    if (!explicit.has(g.frameId)) return g;
  }
  return null;
}
